/**
 * ReaderForm - Reusable form component for creating and editing readers/narrators
 *
 * Supports both create and edit modes with biography text area
 */
package com.castly.admin.ui.readers

import androidx.compose.runtime.*
import com.castly.admin.ui.theme.AppTheme
import com.castly.admin.ui.theme.BorderRadius
import com.castly.admin.ui.theme.LightColors
import com.castly.admin.ui.theme.Spacing
import com.castly.admin.ui.theme.Typography
import com.castly.admin.ui.theme.themeTransition
import com.castly.shared.dto.ReaderDto
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

@Composable
fun ReaderForm(
    reader: ReaderDto? = null,
    isLoading: Boolean = false,
    errorMessage: String? = null,
    onSave: (name: String, biography: String?) -> Unit,
    onCancel: () -> Unit
) {
    val colors = AppTheme.colors
    var name by remember(reader) { mutableStateOf(reader?.name ?: "") }
    var biography by remember(reader) { mutableStateOf(reader?.biography ?: "") }

    val isValid = name.isNotBlank()
    val isEditMode = reader != null

    Div({
        style {
            backgroundColor(Color(colors.surface))
            borderRadius(BorderRadius.lg)
            padding(Spacing.xxl)
            border(1.px, LineStyle.Solid, Color(colors.border))
            themeTransition()
        }
    }) {
        H2({
            style {
                margin(0.px)
                marginBottom(Spacing.lg)
                fontSize(Typography.xl)
                fontWeight("600")
                color(Color(colors.text))
            }
        }) {
            Text(if (isEditMode) "Edit Reader" else "Create Reader")
        }

        // Error message
        errorMessage?.let { error ->
            Div({
                style {
                    backgroundColor(Color(if (colors == LightColors) "#fee2e2" else "#450a0a"))
                    border(1.px, LineStyle.Solid, Color(if (colors == LightColors) "#fca5a5" else "#991b1b"))
                    borderRadius(BorderRadius.md)
                    padding(Spacing.md)
                    marginBottom(Spacing.lg)
                    themeTransition()
                }
            }) {
                Div({
                    style {
                        fontSize(Typography.base)
                        fontWeight("600")
                        color(Color(if (colors == LightColors) "#991b1b" else "#fca5a5"))
                        marginBottom(Spacing.xs)
                    }
                }) {
                    Text("Error")
                }
                Div({
                    style {
                        fontSize(Typography.base)
                        color(Color(colors.danger))
                    }
                }) {
                    Text(error)
                }
            }
        }

        // Name field
        Div({
            style {
                marginBottom(Spacing.lg)
            }
        }) {
            Div({
                style {
                    display(DisplayStyle.Block)
                    fontSize(Typography.base)
                    fontWeight("500")
                    color(Color(colors.text))
                    marginBottom(6.px)
                }
            }) {
                Text("Name")
                Span({
                    style {
                        color(Color(colors.danger))
                        marginLeft(2.px)
                    }
                }) {
                    Text("*")
                }
            }

            Input(InputType.Text) {
                value(name)
                onInput { name = it.value }
                attr("placeholder", "Enter reader/narrator name")
                if (isLoading) disabled()
                style {
                    width(100.percent)
                    padding(10.px, Spacing.md)
                    fontSize(Typography.base)
                    border(1.px, LineStyle.Solid, Color(colors.border))
                    borderRadius(BorderRadius.md)
                    property("outline", "none")
                    property("box-sizing", "border-box")
                    if (isLoading) {
                        backgroundColor(Color(colors.surfaceHover))
                        cursor("not-allowed")
                    }
                }
            }
        }

        // Biography field
        Div({
            style {
                marginBottom(Spacing.xl)
            }
        }) {
            Div({
                style {
                    display(DisplayStyle.Block)
                    fontSize(Typography.base)
                    fontWeight("500")
                    color(Color(colors.text))
                    marginBottom(6.px)
                }
            }) {
                Text("Biography")
            }

            TextArea {
                value(biography)
                onInput { biography = it.value }
                attr("placeholder", "Enter reader biography (optional)")
                rows(6)
                if (isLoading) disabled()
                style {
                    width(100.percent)
                    padding(10.px, Spacing.md)
                    fontSize(Typography.base)
                    border(1.px, LineStyle.Solid, Color(colors.border))
                    borderRadius(BorderRadius.md)
                    property("outline", "none")
                    property("box-sizing", "border-box")
                    property("resize", "vertical")
                    fontFamily("inherit")
                    if (isLoading) {
                        backgroundColor(Color(colors.surfaceHover))
                        cursor("not-allowed")
                    }
                }
            }
        }

        // Action buttons
        Div({
            style {
                display(DisplayStyle.Flex)
                gap(Spacing.md)
                justifyContent(JustifyContent.FlexEnd)
            }
        }) {
            Button({
                onClick { onCancel() }
                if (isLoading) disabled()
                style {
                    padding(10.px, Spacing.xl)
                    fontSize(Typography.base)
                    fontWeight("500")
                    color(Color(colors.text))
                    backgroundColor(Color(colors.surface))
                    border(1.px, LineStyle.Solid, Color(colors.border))
                    borderRadius(BorderRadius.md)
                    cursor(if (isLoading) "not-allowed" else "pointer")
                    property("transition", "all 0.2s")
                }
            }) {
                Text("Cancel")
            }

            Button({
                onClick {
                    if (isValid && !isLoading) {
                        onSave(name, biography.takeIf { it.isNotBlank() })
                    }
                }
                if (!isValid || isLoading) disabled()
                style {
                    padding(10.px, Spacing.xl)
                    fontSize(Typography.base)
                    fontWeight("500")
                    color(Color(colors.surface))
                    backgroundColor(if (!isValid || isLoading) Color(colors.textMuted) else Color(colors.primary))
                    border(0.px)
                    borderRadius(BorderRadius.md)
                    cursor(if (!isValid || isLoading) "not-allowed" else "pointer")
                    property("transition", "all 0.2s")
                }
            }) {
                Text(when {
                    isLoading -> "Saving..."
                    isEditMode -> "Update Reader"
                    else -> "Create Reader"
                })
            }
        }
    }
}
